import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  selectCurrentExpertHighlights,
  selectCurrentRubricSettings,
  selectCurrentHighlights,
  selectIsCurrentUserWorkspaceOwner,
  selectIsGradingViewActive,
  selectSelectedReadingId,
} from "@store/selectors";
import { saveReadingAssessment } from "@store/thunks/workspaceThunks";

const ASSESSMENT_AUTOSAVE_DELAY_MS = 1500;

/**
 * Stage 5: debounced auto-save of the contributor's assessment record.
 *
 * Watches the inputs that influence the computed grade (own highlights
 * + rubric settings + expert reference) and fires a single PUT 1.5s
 * after the last change settles. Guard conditions match the thunk's
 * — see saveReadingAssessment for the full list — but we cheaply
 * short-circuit here to avoid scheduling unnecessary timers.
 *
 * Called from ArgStudioScreen so it runs whenever the annogram view
 * is mounted. The hook returns nothing; effect-only.
 */
export function useAssessmentAutoSave(): void {
  const dispatch = useAppDispatch();
  const selectedReadingId = useAppSelector(selectSelectedReadingId);
  const settings = useAppSelector(selectCurrentRubricSettings);
  const expertHighlights = useAppSelector(selectCurrentExpertHighlights);
  const ownHighlights = useAppSelector(selectCurrentHighlights);
  const isOwner = useAppSelector(selectIsCurrentUserWorkspaceOwner);
  const isGradingView = useAppSelector(selectIsGradingViewActive);

  useEffect(() => {
    // Cheap short-circuits — these match the thunk's guards but save
    // the cost of scheduling and waking up the timer.
    if (!selectedReadingId) return;
    if (isOwner) return;
    if (isGradingView) return;
    if (expertHighlights.length === 0) return;
    if (ownHighlights.length === 0) return;

    const timer = setTimeout(() => {
      dispatch(saveReadingAssessment());
    }, ASSESSMENT_AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [
    dispatch,
    selectedReadingId,
    settings,
    expertHighlights,
    ownHighlights,
    isOwner,
    isGradingView,
  ]);
}
